import React from 'react';
import { useTranslation } from 'react-i18next';

import { NativeStackScreenProps } from '@react-navigation/native-stack';

import { AccountStackParamList } from '@/navigation/types';
import { toLocaleCurrency, toSocialCurrency } from '@/utils/numeric/currency';

import { AccountHistoryContent, AccountHistoryRow } from '../components/AccountHistoryContent';
import { PlayHistoryRange } from '../hooks/use-play-history';
import { WalletHistoryStats, useWalletHistory } from '../hooks/use-wallet-history';

type ScreenProps = NativeStackScreenProps<AccountStackParamList, 'WalletHistory'>;

// Rendered in this order; only the fields present for the wallet are shown.
const SOCIAL_WALLET_FIELDS = [
    { key: 'amount_purchased', label: 'common:purchases' },
    { key: 'betr_cash_awarded', label: 'betr_cash_awarded' },
    { key: 'betr_cash_redeemed', label: 'betr_cash_redeemed' },
    { key: 'amount_bonused', label: 'extra_rewards' },
] as const satisfies readonly { key: keyof WalletHistoryStats; label: string }[];

const FANTASY_WALLET_FIELDS = [
    { key: 'amount_deposited', label: 'amount_deposited' },
    { key: 'amount_withdrawn', label: 'amount_withdrawn' },
    { key: 'amount_bonused', label: 'amount_bonused' },
] as const satisfies readonly { key: keyof WalletHistoryStats; label: string }[];

export const WalletHistoryScreen = ({ route }: ScreenProps) => {
    const { wallet } = route.params;
    const { t } = useTranslation(['account_summary', 'common']);
    const isSocialWallet = wallet === 'social';
    const fields = isSocialWallet ? SOCIAL_WALLET_FIELDS : FANTASY_WALLET_FIELDS;
    const formatAmount = isSocialWallet ? toSocialCurrency : toLocaleCurrency;

    const useRows = (range: PlayHistoryRange) => {
        const { data: stats, isLoading, isError } = useWalletHistory(wallet, range);
        const rows: AccountHistoryRow[] | undefined =
            stats &&
            fields.reduce<AccountHistoryRow[]>((acc, field) => {
                const value = stats[field.key];
                if (value !== undefined) {
                    acc.push({ leftLabel: t(field.label), rightLabel: formatAmount(value) });
                }
                return acc;
            }, []);
        return { rows, isLoading, isError };
    };

    return <AccountHistoryContent title={t(`common:${wallet}`)} heading={t('wallet_history')} useRows={useRows} />;
};
